import React, { useState, useEffect } from 'react';
import { SliderCategory } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<SliderCategory>) => void;
  initial?: Partial<SliderCategory>;
  categories?: SliderCategory[];
}

const DEFAULT_FIELDS = {
  title: true,
  description: true,
  image: true,
  alt: true,
  link: false,
};

const SliderCategoryModal: React.FC<Props> = ({ open, onClose, onSave, initial, categories }) => {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [imageFolder, setImageFolder] = useState(initial?.image_folder ? initial.image_folder.replace(/^\/assets\/images\/slider\//, '') : '');
  const [fields, setFields] = useState<Record<string, boolean>>(
    initial?.fields_structure || DEFAULT_FIELDS
  );
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [folderError, setFolderError] = useState('');

  // Добавленный useEffect для обновления полей при изменении initial
  useEffect(() => {
    setName(initial?.name || '');
    setDescription(initial?.description || '');
    setImageFolder(initial?.image_folder ? initial.image_folder.replace(/^\/assets\/images\/slider\//, '') : '');
    setFields(initial?.fields_structure || DEFAULT_FIELDS);
  }, [initial, open]);

  useEffect(() => {
    if (!name) {
      setNameError('Название обязательно');
    } else if (!/^[a-zA-Zа-яА-Я0-9_-]+$/.test(name)) {
      setNameError('Только буквы, цифры, дефис, подчёркивание');
    } else if (categories && categories.some(cat => cat.name === name && (!initial || cat.id !== initial.id))) {
      setNameError('Категория с таким названием уже существует');
    } else {
      setNameError('');
    }
  }, [name, categories, initial]);

  useEffect(() => {
    if (!imageFolder) {
      setFolderError('Папка обязательна');
    } else {
      setFolderError('');
    }
  }, [imageFolder]);

  const handleFieldChange = (field: string) => {
    setFields(f => ({ ...f, [field]: !f[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nameError || folderError) return;
    setSaving(true);
    try {
      await onSave({
        name,
        description,
        image_folder: imageFolder,
        fields_structure: fields,
      });
    } catch (err: any) {
      const details = err?.response?.data?.details || '';
      if (details.includes('image_folder') && details.includes('SQLSTATE 23505')) {
        setFolderError('Папка уже используется в другой категории');
      } else {
        setFolderError('Ошибка: ' + (err?.response?.data?.error || 'Не удалось создать категорию'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onMouseDown={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-4">{initial ? 'Редактировать' : 'Создать'} категорию слайдера</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Название</label>
            <input type="text" className="w-full border rounded px-3 py-2" value={name} onChange={e => setName(e.target.value)} required />
            {nameError && <div className="text-xs text-red-600 mt-1">{nameError}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Описание</label>
            <input type="text" className="w-full border rounded px-3 py-2" value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Папка для картинок</label>
            <input type="text" className="w-full border rounded px-3 py-2" value={imageFolder} onChange={e => setImageFolder(e.target.value)} required pattern="^[a-zA-Zа-яА-Я0-9_-]+$" placeholder="Введите название папки" />
            {folderError && <div className="text-xs text-red-600 mt-1">{folderError}</div>}
            <div className="text-xs text-gray-500 mt-1">Только латиница, цифры, дефис, подчёркивание. Пример: news</div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Поля слайда</label>
            <div className="flex flex-wrap gap-4 mt-1">
              {Object.entries(DEFAULT_FIELDS).map(([field, _]) => (
                <label key={field} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={fields[field]} onChange={() => handleFieldChange(field)} />
                  {field === 'title' && 'Заголовок'}
                  {field === 'description' && 'Описание'}
                  {field === 'image' && 'Картинка'}
                  {field === 'alt' && 'Alt'}
                  {field === 'link' && 'Ссылка'}
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded" disabled={saving || !!nameError || !!folderError}>{initial ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SliderCategoryModal; 